<template>
  <div id="student-dgt-report" :class="{iphonex: iphonex}" v-if="show">
    <navigation-top-app :app="app" :back="close">
      <template v-slot:center>
        <div id="navigation-center">
          {{ title }}
        </div>
      </template>
    </navigation-top-app>
    <!-- Each page of the PDF drawn on a canvas with pdf.js (vue-pdf left with
         the move to webpack 5 and the preview stayed blank, 3.9 of Miguel). -->
    <div id="student-dgt-report-pdf">
      <loader-dot v-if="loading" :size="'16px'" :color_1="'rgb(32, 113, 183)'" :color_2="'rgb(32, 113, 183)'" />
      <p class="report-error" v-if="error">{{ app.strings.app.student.dgtreport.preview_error }}</p>
      <div ref="pages"></div>
    </div>
    <div class="bottom-action">
      <button class="h-button w-100" v-on:click="download()">
        {{ app.strings.common.download }}
      </button>
      <button class="h-button w-100 report-print" v-if="canPrint" v-on:click="print()">
        {{ app.strings.app.student.dgtreport.print }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'student-dgt-report',
  data () {
    return {
      show: false,
      loading: false,
      error: false,
      url: '',
      title: ''
    }
  },
  props: {
    'app': {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  computed: {
    iphonex () {
      return !!(this.app.config && this.app.config.style && this.app.config.style.iphonex)
    },
    // The app's webview cannot print.
    canPrint () {
      return !window.ReactNativeWebView
    }
  },
  beforeDestroy () {
    this.stopResize()
  },
  methods: {
    open (url, title) {
      this.url = url
      this.title = title || this.app.strings.app.student.dgtreport.title
      this.show = true
      this.pdf = null
      this.file = null
      this.width = 0
      this.$nextTick(() => this.render())
      this.onResize = () => {
        clearTimeout(this.resizeTimer)
        this.resizeTimer = setTimeout(() => this.draw(), 250)
      }
      window.addEventListener('resize', this.onResize)
    },
    stopResize () {
      clearTimeout(this.resizeTimer)
      if (this.onResize) {
        window.removeEventListener('resize', this.onResize)
        this.onResize = null
      }
    },
    async render () {
      this.loading = true
      this.error = false
      try {
        // Loaded only here: pdf.js is big. The worker entry leaves the worker
        // in window.pdfjsWorker, so pdf.js runs it in the page (no worker file
        // to serve, also in the app's offline copy).
        const pdfjs = await import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/build/pdf')
        await import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/build/pdf.worker.entry')
        this.pdf = await pdfjs.getDocument({url: this.url}).promise
        await this.draw()
      } catch (e) {
        this.error = true
      }
      this.loading = false
    },
    // Draws the pages for the current width; a rotation or a new window size
    // draws them again.
    async draw () {
      const box = this.$refs.pages
      if (!box || !this.pdf) {
        return
      }
      const width = Math.min(box.parentNode.clientWidth - 24, 900)
      if (width === this.width) {
        return
      }
      this.width = width
      const ratio = window.devicePixelRatio || 1
      const pages = document.createDocumentFragment()
      for (let n = 1; n <= this.pdf.numPages; n++) {
        const page = await this.pdf.getPage(n)
        const base = page.getViewport({scale: 1})
        const viewport = page.getViewport({scale: (width / base.width) * ratio})
        const canvas = document.createElement('canvas')
        canvas.width = viewport.width
        canvas.height = viewport.height
        canvas.style.width = (viewport.width / ratio) + 'px'
        pages.appendChild(canvas)
        await page.render({canvasContext: canvas.getContext('2d'), viewport: viewport}).promise
      }
      // A newer size arrived while drawing: its own draw puts the pages.
      if (width !== this.width) {
        return
      }
      box.querySelectorAll('canvas').forEach(c => c.remove())
      box.appendChild(pages)
    },
    async readFile () {
      if (!this.file) {
        const response = await fetch(this.url)
        if (!response.ok) {
          throw new Error('status-' + response.status)
        }
        this.file = await response.blob()
      }
      return URL.createObjectURL(this.file)
    },
    async download () {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          action: 'openlink',
          url: this.url
        }))
        return
      }
      try {
        const file = await this.readFile()
        const link = document.createElement('a')
        link.href = file
        link.download = this.app.strings.app.student.dgtreport.file_name + '.pdf'
        document.body.appendChild(link)
        link.click()
        link.remove()
        setTimeout(() => URL.revokeObjectURL(file), 60000)
      } catch (e) {
        window.open(this.url, '_blank')
      }
    },
    async print () {
      try {
        const file = await this.readFile()
        const frame = document.createElement('iframe')
        frame.style.display = 'none'
        frame.onload = () => {
          frame.contentWindow.focus()
          frame.contentWindow.print()
        }
        frame.src = file
        document.body.appendChild(frame)
        setTimeout(() => {
          frame.remove()
          URL.revokeObjectURL(file)
        }, 60000)
      } catch (e) {
        window.open(this.url, '_blank')
      }
    },
    close () {
      this.stopResize()
      this.pdf = null
      this.file = null
      this.show = false
      this.$emit('onClose')
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/variables-app.scss';
@import '@/assets/styles/variables-valkiria.scss';

#student-dgt-report {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1100;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #e8edf3;
  box-sizing: border-box;
}

#student-dgt-report-pdf {
  position: relative;
  flex: 1;
  overflow: auto;
  margin: 60px 0 70px;
}

#student-dgt-report.iphonex #student-dgt-report-pdf {
  margin-top: 80px;
}

// Only where there is a side menu, the only place it is useful.
.report-print {
  display: none;
}

.bottom-action > button + button {
  margin-left: 12px !important;
}

@media (min-width: 1024px) {
  #student-dgt-report {
    left: 264px;
    width: calc(100% - 264px);
  }

  .report-print {
    display: block;
  }
}

#student-dgt-report-pdf > .loader-dot {
  position: absolute;
}

#student-dgt-report-pdf ::v-deep canvas {
  max-width: calc(100% - 24px);
  display: block;
  margin: 12px auto;
  background-color: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
}

.report-error {
  margin: 40px 16px;
  color: #475569;
  text-align: center;
}
</style>
